import { Component, Input, Output, EventEmitter, HostBinding } from '@angular/core';

@Component({
  selector: 'ds-lib-list-with-actions',
  templateUrl: './ds-lib-list-with-actions.component.html',
  styleUrls: ['./ds-lib-list-with-actions.component.scss'],
})
/**
 * Generic list component that can display any list of items with the help of the properties input.
 * It can display edit/delete buttons, icons on the left of the text.
 */
export class DsLibListWithActionsComponent {
  /**
   *
   */
  @HostBinding('class.user-select-none') userSelectNone = true;

  /**
   * Sets whether the user can select the text of the component.
   * By default, the user cannot select.
   *
   * @param userSelect selected user
   */
  @Input() set userSelect(userSelect: boolean) {
    this.userSelectNone = userSelect;
  }

  /**
   * List of items that will be displayed as a list
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() items: any[] = [];

  /**
   * Properties
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() properties: any = { name: null, cannotDelete: false };

  /**
   * Whether to display the delete icon and emit the onDelete event.
   */
  @Input() canDelete = false;

  /**
   * Whether to display the edit icon and emit the onEdit event.
   */
  @Input() canEdit = false;

  /**
   * Selection mode. Multiple means that several rows can be selected at once, while single will limit select items to 1.
   * None means that no item can be selected.
   */
  @Input() selectMode: 'multiple' | 'single' | 'none' = 'single';

  /**
   * Whether to display an icon on the left
   */
  @Input() leftIcon = '';

  /**
   *
   */
  @Input() leftIconColor = 'grey';

  /**
   * Whether to display a chevron on the right side for each row of the list
   */
  @Input() rightChevron = false;

  /**
   * Clicking outside the component will unselect all the rows if TRUE.
   */
  @Input() unselectOnBlur = true;

  /**
   * selecting the same row 2 times will unselect it if TRUE.
   */
  @Input() toggleSelect = true;

  /**
   * Tooltip to display when we can delete
   */
  @Input() canDeleteTooltip = '';

  /**
   * Tooltip to display when we cannot delete
   */
  @Input() cannotDeleteTooltip = '';

  /**
   * Display name inside a tooltip if overflow (name is bigger than X characters). 0 disables tooltip
   */
  @Input() tooltipOnNameOverflow = 0;

  /**
   * Emits the selected item on select
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectItem = new EventEmitter<any>();

  /**
   * Double-way binding.
   * List of selected items. If selectMode is single, the list will always be of length 0 or 1
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() selectedItems: any[] = [];

  /**
   * Emits selected items array on item select
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  /**
   * Emits the item to delete
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() delete = new EventEmitter<any>();

  /**
   * Emits the item to edit
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() edit = new EventEmitter<any>();

  /**
   * Set font size to detect name overflow and display tooltip
   */
  fontSize = 9;

  /**
   * Select the current row.
   *
   * @param item the selected row
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect(item: any) {
    let itemToEmit = item;
    // Only one item can be selected at any given time
    if (this.selectMode === 'single') {
      if (this.isItemSelected(item)) {
        if (this.toggleSelect) {
          this.selectedItems = [];
          itemToEmit = null;
        }
      } else {
        this.selectedItems = [item];
      }
    }
    if (this.selectMode === 'multiple') {
      if (this.isItemSelected(item)) {
        // toggle select always enabled for multiple.
        this.selectedItems = this.selectedItems.filter((selectedItem) => selectedItem !== item);
        itemToEmit = null;
      } else {
        this.selectedItems.push(item);
      }
    }
    // do not emit if selectMode is none
    if (this.selectMode === 'none') {
      return;
    }

    this.selectItem.emit(itemToEmit);
    this.emitSelectedItems();
  }

  /**
   * Returns whether the item is present inside the selectedItems array.
   *
   * @param item selected item
   * @returns boolean
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isItemSelected(item: any): boolean {
    return this.selectedItems.includes(item);
  }

  /**
   * Emits onDelete event with the item to delete.
   *
   * @param item selected item
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDelete(item: any) {
    this.delete.emit(item);
  }

  /**
   * Emits edit event with the item to edit.
   *
   * @param item selected item
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEdit(item: any) {
    this.edit.emit(item);
  }

  /**
   * Unselects all items and emits. Function triggered only on component blur.
   * If unselectOnBlur = FALSE, does not reset selectedItems and does not emit.
   */
  unselectItems() {
    if (!this.unselectOnBlur) {
      return;
    }
    this.selectedItems = [];
    this.selectItem.emit(null);
    this.emitSelectedItems();
  }

  /**
   * Emits selected items array.
   */
  emitSelectedItems() {
    this.selectedItemsChange.emit(this.selectedItems);
  }
}
